import "./account.css";

import EventEmitter from "eventemitter3";
import { Data } from "../../../models/data";
import { Page } from "../page";
import { EventTypes } from "../../../core/event-bus";
import { User } from "../../../models/user";
import { Util } from "../../../utils/util";
import { Modals, OrderType, Pages } from "../../../types/enums";
import { ILocation, ILocationProps } from "../Locations/locations.interface";
import { ProfileService } from "../../../services/profile-service";
import { IUser, IUserCard, IUserNextOrderCredit } from "../../../models/user.interface";
import { LoaderSpinner } from "../../shared/LoaderSpinner/loader-spinner";
import { Dialog } from "../../modals/Dialog/dialog";
import { TimedMessage } from "../../shared/TimedMessage/timed-message";
import { Context } from "../../../models/context";
import { LocalizationService } from "../../../services/localization-service";
import { DataLayerService } from '../../../services/data-layer-service';
import { IDataCoupon } from "../../../models/data.interface";
import { Order } from "../../../models/order";
import { HistoryRow, HistoryEntry } from "./account.interface";
import { Router } from '../../../core/router';

export class Account extends Page {
    private readonly _user: User;
    private readonly _profileService: ProfileService;
    private readonly _order: Order;
    private readonly _dataLayer: DataLayerService;

    private readonly _contactEdit: HTMLElement | null;
    private readonly _accountName: HTMLElement | null;
    private readonly _accountPhone: HTMLElement | null;
    private readonly _accountEmail: HTMLElement | null;
    private readonly _accountBirthday: HTMLElement | null;
    private readonly _changePassword: HTMLElement | null;

    private readonly _deliveryEdit: HTMLElement | null;
    private readonly _accountDelivery: HTMLElement | null;
    private readonly _carryEdit: HTMLElement | null;
    private readonly _accountCarry: HTMLElement | null;
    private readonly _accountPayment: HTMLElement | null;
    private readonly _accountNotificationsEmail: HTMLInputElement | null;
    private readonly _accountNotificationsText: HTMLInputElement | null;
    private readonly _accountCreditHistory: HTMLElement | null;
    private readonly _accountDelete: HTMLElement | null;

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, user: User, profileService: ProfileService,
        order: Order, dataLayer: DataLayerService) {
        super("page_account", eventBus, data, context, localizationService);

        this._user = user;
        this._profileService = profileService;
        this._order = order;
        this._dataLayer = dataLayer;

        this._contactEdit = document.getElementById("account_contact_edit");
        this._accountName = document.getElementById("account_name_value");
        this._accountPhone = document.getElementById("account_phone_value");
        this._accountEmail = document.getElementById("account_email_value");
        this._accountBirthday = document.getElementById("account_birthday_value");
        this._changePassword = document.getElementById("btn_account_change_password");

        this._deliveryEdit = document.getElementById("account_delivery_edit");
        this._accountDelivery = document.getElementById("account_delivery_value")
        this._carryEdit = document.getElementById("account_carry_edit");
        this._accountCarry = document.getElementById("account_carry_value");
        this._accountPayment = document.getElementById("account_payment_value")
        this._accountNotificationsEmail = document.getElementById("account_notification_email_value") as HTMLInputElement;
        this._accountNotificationsText = document.getElementById("account_notification_text_value") as HTMLInputElement;
        this._accountCreditHistory = document.getElementById("account_credit_history_value");
        this._accountDelete = document.getElementById("btn_account_delete");

        this._eventBusListeners();
    }

    public async init(): Promise<void> {
        super.init();
        this._eventListeners();
    }

    public override show() {
        super.show();
        if (!this._user.isLoggedIn()) this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LANDING);

        this._context.setProperty("upsellSelected", false);
        if (this._dataLayer)
            this._dataLayer.page_view(this._profileService.user.isLoggedIn(), this._data.storeKey ? this._data.getProperty("StoreInfo").storeCode.toString() : '0', this._profileService.user.user);
    }

    private _eventBusListeners = () => {
        this._eventBus.on(EventTypes.LOGGED_IN, () => this._renderPage());
        this._eventBus.on(EventTypes.LOGGED_OUT, this._redirectToHome);
        this._eventBus.on(EventTypes.ORDER_COMPLETED, () => this._renderPage());
        this._eventBus.on(EventTypes.USER_CHANGED, () => this._user.isLoggedIn() && this._renderPage());
        this._eventBus.on(EventTypes.DATA_RETRIEVED, () => this._user.isLoggedIn() && this._renderPage());
        this._eventBus.on(EventTypes.LANGUAGE_CHANGED, () => this._renderPage());
    }

    private _eventListeners = () => {
        this._contactEdit?.addEventListener("click", () =>
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.ACCOUNT_CONTACT)
        )

        this._carryEdit?.addEventListener("click", () => {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS, {
                forOrderType: OrderType.TAKE_OUT,
                nearbyOnly: true,
                forChangingStore: false,
                forSettingPreferredStore: true,
                originPage: Pages.ACCOUNT,
                isSave: true,
                onContinue: this._updateCarryOutStore })
        })

        const updateDelivery = async (item: ILocation) => {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.ACCOUNT)

            if (!item?.tempAddress) return;

            const spinner = new LoaderSpinner(this._element);
            const success = await this._profileService.addDeliveryAddress(item.tempAddress);

            if (success) {
                await this._profileService.signInJWT();
            }

            spinner.destroy();
        }

        this._deliveryEdit?.addEventListener("click", () => {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS, {
                forOrderType: OrderType.DELIVERY,
                nearbyOnly: true,
                onContinue: updateDelivery,
                hideMap: true,
                isSave: true,
                originPage: Pages.ACCOUNT
            } as ILocationProps);
        })

        this._changePassword?.addEventListener("click", () => {
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.ACCOUNT_CHANGE_PASSWORD)
        })

        this._accountNotificationsEmail?.addEventListener("input", async (e: Event) => {
            const user = this._profileService.user.user;

            const element = e.target as HTMLInputElement;
            user.IsMarketingEmail = element.checked ?? false;

            await this._updateProfileInfoAsync(user);
        })

        this._accountNotificationsText?.addEventListener("input", async (e: Event) => {
            const user = this._profileService.user.user;

            const element = e.target as HTMLInputElement;
            user.IsMarketingText = element.checked ?? false;

            await this._updateProfileInfoAsync(user);
        })

        this._accountDelete?.addEventListener("click", () => {
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.ACCOUNT_CONFIRM_DELETE);
        })
    }

    private async _updateProfileInfoAsync(user: IUser): Promise<void> {
        const spinner = new LoaderSpinner();
        const success = await this._profileService.saveProfile(user, () => { }, false);

        if (success && this._dataLayer)
            this._dataLayer.update_profile(user);

        spinner.destroy();
    }

    private readonly _updateCarryOutStore = async (item: ILocation) => {
        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.ACCOUNT)
        const user = this._profileService.user.user;
        const store = this._data.locations.find(s => s.storeKey == item.selectedStore);

        if (!store) return;

        user.FavoriteStoreId = store.storeId;

        if (this._element) {
            const spinner = new LoaderSpinner(this._element);
            const success = await this._profileService.saveProfile(user, () => { }, false);

            if (success) {
                await this._profileService.signInJWT();
            }

            spinner.destroy();

            let msg: TimedMessage | null = null;

            if (success)
                msg = new TimedMessage(this._localizationService.names("PreferredCarryOutStoreUpdateSuccess"), "", 1500);
            else
                msg = new TimedMessage(this._localizationService.names("PreferredCarryOutStoreUpdateFailure"), "", 1500);

            msg?.show();
        }
    }

    public async initAsync(): Promise<void> {
    }

    private _elementPayment(payment: IUserCard): HTMLElement {
        const element = document.createElement("div");
        element.classList.toggle("payment-type", true);

        const details = document.createElement("div");
        details?.classList.toggle("details");

        // 2DO: Update this to include the card type
        const name = document.createElement("div");
        name?.classList.toggle("name");
        name.innerText = `${Util.getCreditCardTypeById(payment.cardTypeId)}...${payment.last4}`;

        const expiration = document.createElement("div");
        expiration?.classList.toggle("expiration");
        expiration.innerText = `${this._localizationService.names("Expires")}: ${payment.expirationMonthNumber} / ${payment.expirationYearNumber}`

        const remove = document.createElement("div")
        remove?.classList.toggle("remove");
        remove.innerText = this._localizationService.names("Delete");
        remove.setAttribute("role", "button");
        remove.setAttribute("tabindex", "0");
        remove.setAttribute("aria-label", `${this._localizationService.names("Delete")} ${Util.getCreditCardTypeById(payment.cardTypeId)} ending in ${payment.last4}`);

        details.appendChild(name);
        details.appendChild(expiration);
        element.appendChild(details);
        element.appendChild(remove);

        const removePayment = async () => {
            const user = this._user.user;
            const index = user.Wallet.findIndex(c => c.token === payment.token)

            if (index == -1)
                return;

            const dialog = new Dialog(
                `${this._localizationService.names("DeletePaymentConfirm")} ${payment.last4}?`,
                [
                    { label: this._localizationService.names("No"), value: "no" },
                    { label: this._localizationService.names("Yes"), value: "yes", action: () => this._deletePaymentAsync(user, index) }
                ]
            );
        }

        remove.addEventListener("click", removePayment)
        Util.addKeyboardActivation(remove, removePayment)

        return element;
    }

    private readonly _deletePaymentAsync = async (user: IUser, index: number): Promise<void> => {
        const spinner = new LoaderSpinner(this._element);
        const success = await this._profileService.deleteWallet(user.Wallet[index].last4)
        spinner.destroy();

        if (success) {
            const message = new TimedMessage(this._localizationService.names("DeletePaymentSuccess"), "", 1500);
            message.show();
            await this._profileService.getUserWalletAsync()
        }
        else {
            const message = new TimedMessage(this._localizationService.names("DeletePaymentFailure"), "", 1500);
            message.show();
        }
    }

    private _renderPage = () => {
        this._renderPersonalInfoSection();
        this._renderAddressesSection();
        this._renderPaymentInfoSection();
        this._renderPreferencesSection();
        this._renderCreditHistorySection();
    }

    private readonly _renderPersonalInfoSection = (): void => {
        const name = this._user.getProperty("Name") + " " + this._user.getProperty("LastName")
        if (name && this._accountName) {
            this._accountName.innerText = name;
        }

        const phone = this._user.getProperty("Phone");
        if (phone && this._accountPhone) {
            this._accountPhone.innerText = Util.formatPhoneNumberByCountryId(phone, this._user.getProperty("CountryCodeId") || 1, this._data.getBrandProperty("Countries"))
        }

        const email = this._user.getProperty("Email");
        if (email && this._accountEmail) {
            this._accountEmail.innerText = email;
        }

        if (this._accountBirthday) {
            const birthday = this._user.getProperty("BirthDate");
            const culture = this._context.getProperty("languageCode");

            const birthdayText = birthday?.toLocaleString(culture, { month: "short", day: "numeric" }) ?? "";
            this._accountBirthday.innerText = (birthday && culture) ? birthdayText : "";
        }
    }

    private _renderAddressesSection = (): void => {
        this._accountDelivery && (this._accountDelivery.innerHTML = `<div class="empty" ltag="NoPreferredDeliveryAddress">${this._localizationService.names("NoPreferredDeliveryAddress")}</div>`)
        const delivery = this._user.getProperty("Addresses")?.find(a => a.IPRM);
        if (delivery && this._accountDelivery) {
            const address = Util.formatAddressObject({
                StreetNo: delivery.STRNO,
                Address1: delivery.ADDR1,
                Address2: delivery.ADDR2,
                Address3: delivery.ADDR3,
                Address4: delivery.ADDR4,
                Address5: delivery.ADDR5,
                City: delivery.CITY,
                State: delivery.STA,
                ZipCode: delivery.ZIP,
                CountryID: delivery.CID || 1,
                AddressTypeID: delivery.ATID
            }, this._data.getBrandProperty("Countries"));

            this._accountDelivery.innerHTML = `<span>${address.address1}${address.address2 ? address.address2 : ""},</span></br><span>${address.cityState}</span>`;
        }

        this._accountCarry && (this._accountCarry.innerHTML = `<div class="empty" ltag="NoPreferredCarryOutStore">${this._localizationService.names("NoPreferredCarryOutStore")}</div>`)
        const takeOutStoreId = this._user.getProperty("FavoriteStoreId");
        const store = this._data.locations.find(s => s.storeId === takeOutStoreId)
        if (store && this._accountCarry) {
            const address = Util.formatAddressObject({
                StreetNo: store.streetNumber,
                Address1: store.address1,
                Address2: store.address2,
                Address3: store.address3,
                Address4: store.address4,
                Address5: store.address5,
                City: store.city,
                State: store.state,
                ZipCode: store.zip,
                CountryID: store.countryId || 1,
                AddressTypeID: 0
            }, this._data.getBrandProperty("Countries"));

            this._accountCarry.innerHTML = `<span>${address.address1}</span></br><span>${address.cityState}</span>`;
        }
    }

    /**
     * Renders saved wallet payment methods into the account payment section.
     *
     * Shows a localized empty-state message when no wallet cards exist. When cards are present,
     * clears the section, removes duplicate cards by processor/last4/expiration, and appends
     * one payment element per unique wallet card.
     */
    private _renderPaymentInfoSection(): void {
        if (this._accountPayment)
            this._accountPayment.innerHTML = `<div class="empty" ltag="NoSavedPaymentMethods">${this._localizationService.names("NoSavedPaymentMethods")}</div>`;

        const wallet = this._user.getProperty("Wallet");
        if (!wallet?.length || !this._accountPayment)
            return;

        this._accountPayment.innerHTML = "";

        const uniqueWallet = wallet.filter((card, index, arr) =>
            index === arr.findIndex(x =>
                x.processorId === card.processorId &&
                x.last4 === card.last4 &&
                x.expirationMonthNumber === card.expirationMonthNumber &&
                x.expirationYearNumber === card.expirationYearNumber
            )
        );

        uniqueWallet.forEach(card => {
            this._accountPayment?.appendChild(this._elementPayment(card));
        });
    }

    private _renderPreferencesSection(): void {
        const notificationsEmail = this._user.getProperty("IsMarketingEmail");
        if (notificationsEmail && this._accountNotificationsEmail) {
            this._accountNotificationsEmail.checked = notificationsEmail;
        }

        const notificationsText = this._user.getProperty("IsMarketingText");
        if (notificationsText && this._accountNotificationsText) {
            this._accountNotificationsText.checked = notificationsText;
        }
    }

    /**
     * Renders the credit history section if Online Next Order Credit is enabled and the user has credits to display
     * @returns
     */
    private _renderCreditHistorySection(): void {
        if (this._canShowCreditHistory())
        {
            const rows = this._getAllHistoryRows();
            this._renderRows(rows);
            Util.showElement("account_credit_history");
        }
        else {
            Util.hideElement("account_credit_history");
        }
    }

    /**
     * Builds and sorts all credit history rows by date (newest first).
     * @returns {HistoryRow[]} Array of sorted history rows.
     */
    private _getAllHistoryRows(): HistoryRow[] {
        const credits: IUserNextOrderCredit[] = this._user.getProperty("NextOrderCredits") ?? [];
        const rows: HistoryRow[] = [];

        for (const credit of credits) {
            const coupon = this._order.couponGetCoupon(credit.CouponID, true, this._order.getProperty("OrderTypeID"), null);
            if (!coupon) continue;

            for (const entry of this._getHistoryEntriesFor(credit, coupon)) {
                rows.push({
                    date: entry.date,
                    element: this._buildCreditHistoryRow(
                        entry.credit, entry.coupon, entry.event, entry.date
                    )
                });
            }
        }

        return rows.sort((a, b) => b.date.getTime() - a.date.getTime()); // newest first
    }

    /**
     * Checks if the credit history section can be shown.
     * @returns {boolean} True if feature is enabled, container exists, and a store is selected.
     */
    private _canShowCreditHistory(): boolean {
        const hasFeature = this._data.getBrandProperty("Settings").OOS.showNextOrderCredit;
        const hasDOM = !!this._accountCreditHistory;
        const hasStoreKey = this._data.storeKey !== "";
        return hasFeature && hasDOM && hasStoreKey;
    }

    /**
     * Creates history entries (Received, Redeemed, Expired) for a given credit.
     * @param {IUserNextOrderCredit} credit - The next-order credit record.
     * @param {IDataCoupon} coupon - The associated coupon data.
     * @returns {HistoryEntry[]} List of history entries for the credit.
     */
    private _getHistoryEntriesFor(
        credit: IUserNextOrderCredit,
        coupon: IDataCoupon
    ): HistoryEntry[] {
        const now = new Date();
        const entries: HistoryEntry[] = [
            { event: "Received", date: new Date(credit.AddedDate), credit, coupon },
        ];

        if (credit.Used && credit.UsedDate) {
            entries.push({ event: "Redeemed", date: new Date(credit.UsedDate), credit, coupon });
        } else if (!credit.Used && new Date(credit.ExpirationDate) < now) {
            entries.push({ event: "Expired", date: new Date(credit.ExpirationDate), credit, coupon });
        }

        return entries;
    }

    /**
     * Builds the credit history row
     * @param {IUserNextOrderCredit} credit - the next order credit data
     * @param {IDataCoupon} coupon - the coupon associated with the next order credit
     * @returns {HTMLElement} - The generated HTML element.
     */
    private _buildCreditHistoryRow(credit: IUserNextOrderCredit, coupon: IDataCoupon, event: "Received" | "Redeemed" | "Expired",
      eventDate: Date): HTMLElement {
        const element = document.createElement("div");
        element.classList.toggle("credit-history-item", true);

        const nameSpan = document.createElement("span");
        nameSpan.classList.add("name");
        nameSpan.innerText = `${event} "${this._localizationService.GetName(coupon.Names)}"`;

        const dateSpan = document.createElement("span");
        dateSpan.classList.add("date");
        dateSpan.innerText = this._getFormattedCreditHistoryDate(eventDate);

        element.append(nameSpan, dateSpan);
        return element;
    }

    /**
     * Inserts given rows into the DOM or shows a message when empty
     * @param {HistoryRow[]} rows - The rows to render.
     * @returns {void}
     */
    private _renderRows(rows: HistoryRow[]): void {
        this._accountCreditHistory!.innerHTML = "";

        if (rows.length == 0) {
            this._accountCreditHistory!.innerHTML = `
                <div class="empty" ltag="NoCreditHistory">${this._localizationService.names("NoCreditHistory")}</div>
            `;
            return;
        }

        for (const row of rows) {
            this._accountCreditHistory!.appendChild(row.element);
        }
    }

    /**
     * Formats a Date into the user's locale with a "Month Day, Year" pattern or returns "Unknown Date" if no date provided.
     * @param {Date} date - The date to format.
     * @returns {string} The formatted date string or "Unknown Date".
     */
    private _getFormattedCreditHistoryDate(date: Date): string {
        const options: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "long",
            day: "numeric",
        };

        const formattedDate = date ? Util.formatDateWithOptions(
            Util.formatDate(date),
            this._profileService.user.getProperty("CultureCode") ?? "en-US",
            options
        ) : "Unknown Date";

        return formattedDate;
    }

    private readonly _redirectToHome = () => {
        if (Router.path === Pages.ACCOUNT)
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LANDING);
    }
}   